<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('productProfile.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('productProfile.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div class="ts-space-x-3">
            <ts-button outline color="info" @click.prevent="addNew">
              {{ $t('add_new') }}
            </ts-button>
            <ts-button class="ts-capitalize" outline @click.prevent="allSerial">
              {{ $t('productProfile.ViewAllSerial') }}
            </ts-button>
            <ts-button
              outline
              :waiting="exportingPlus"
              @click.prevent="exportPlus"
            >
              {{ $t(exportingPlus ? 'productProfile.exportPlusProcessing' : 'productProfile.exportPlus') }}
            </ts-button>
          </div>
          <div class="d-flex">
            <ts-input
              v-tooltip="$t('productProfile.search')"
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px;"
              class="inline-block"
            />
          </div>
        </div>
      </ts-card-wrapper>
      <div class="overflow-auto">
        <Table
        :columns="columns"
        :data="groupResources"
        :span-method="handleSpan"
        row-key="index"
        size="small"
        :loading="loading"
        highlight-row
        stripe
        max-height="700"
      >
        <template #group_name_en="{ row, index }">
          {{ row.group ? row.group.pro_group_name_en : '' }}
        </template>
        <template #uom_name_en="{ row, index }">
          {{ row.uom_category.uom_cat_name_en }}
        </template>
        <template #product_type="{ row, index }">
          {{ row.type.product_type_en }}
        </template>
        <template #vendor_name="{ row, index }">
            <table class="vendor-list">
                  <tr v-for="(item, index) in row.vendors" :key="index">
                    <td style="border-bottom: none;">{{ item.vendor_name }}</td>
                  </tr>
            </table>
        </template>
        <template #is_expirable="{ row, index }">
          <span class="badge badge-success" v-if="row.is_expirable"
                >Yes</span
              >
              <span class="badge badge-danger" v-else>No</span>
        </template>
        <template #is_warranty="{ row, index }">
          <span class="badge badge-success" v-if="row.is_warranty"
                >Yes</span
              >
              <span class="badge badge-danger" v-else>No</span>
        </template>
        <template #is_available_for_sale="{ row, index }">
          <a-switch
                v-model="row.is_available_for_sale"
                size="small"
                :loading="row.loading_sale"
                @change="allowForSale(row)"
              />
        </template>
        <template #is_calculate_stock="{ row, index }">
          <a-switch
                v-model="row.is_calculate_stock"
                size="small"
                :loading="row.loading_stock"
                @change="calculate(row)"
              />
        </template>
        <template #action="{ row, index }">
              <a
                v-if="row.is_generate_unique_serial"
                href="#"
                v-tooltip="$t('productProfile.viewSerial')"
                class="text-primary"
                @click.prevent="viewSeril(row)"
              >
                <i class="fas fa-list-ul"></i>
              </a>
              <a
                href="#"
                v-tooltip="$t('edit')"
                class="text-primary mr-2 ml-2"
                @click.prevent="edit(row)"
              >
                <i class="fas fa-edit"></i>
              </a>
              <a-popconfirm
                placement="leftTop"
                :title="$t('are_you_sure_to_delete')"
                :ok-text="$t('yes')"
                :cancel-text="$t('no')"
                @confirm="deleteRecord(row)"
              >
                <a href="#" :disabled="row._deleting">
                  <i
                    class="fas fa-circle-notch spin"
                    v-if="row._deleting"
                  ></i>
                  <i class="fa fa-trash text-red" v-else></i>
                </a>
              </a-popconfirm>
        </template>
      </Table>
      </div>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState, mapActions } from 'vuex'
import iTable from '@/store/i-table-helper'
import * as FileDownload from 'downloadjs'

export default {
  name: 'profileIndex',
  data() {
    return {
      loading: false,
      exportingPlus: false,
      showForm: false
    }
  },
  computed: {
    ...mapState('product/profile', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.product.profile.search
      },
      set(newValue) {
        this.$store.commit('product/profile/SET_SEARCH', newValue)
        this.$store.commit('product/profile/RESET_CURRENT_PAGE')
      }
    },
    groupResources() {
      return iTable.rowGroup(this.resources, 'product_category.category_name_en')
    },
    columns() {
      return [
        {
          key: 'column_name',
          tree: true,
          maxWidth: 15
        },
        { 
          title: this.$t('productProfile.barcode'),
          key: 'barcode',
          width: 130
        },
        { 
          title: this.$t('productProfile.group'),
          slot: 'group_name_en',
          width: 140  
        },
        { 
          title: this.$t('productProfile.nameKh'),
          key: 'product_name_kh',
          whiteSpace: 'nowrap',
          width: 330 
        },
        { 
          title: this.$t('productProfile.nameEn'),
          key: 'product_name_en',
          width: 330 
        },
        { 
          title: this.$t('productProfile.uomCategory'),
          slot: 'uom_name_en',
          width: 140 
        },
        { 
          title: this.$t('productProfile.productType'),
          slot: 'product_type',
          width: 190
        },
        { 
          title: this.$t('productProfile.supplier'),
          slot: 'vendor_name',
          width: 160
        },
        {
          title: this.$t('productProfile.expirationAvailable'),
          slot: 'is_expirable',
          width: 110,
          align: 'center'
        },
        {
          title: this.$t('productProfile.isWarranty'),
          slot: 'is_warranty',
          width: 110,
          align: 'center'
        },
        {
          title: this.$t('productProfile.saleAvailable'),
          slot: 'is_available_for_sale',
          width: 110,
          align: 'center'
        },
        {
          title: this.$t('productProfile.calculateStock'),
          slot: 'is_calculate_stock',
          width: 110,
          align: 'center'
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          width: 120
        }
      ]
    }
  },
  methods: {
    ...mapActions('product/profile', ['saleAvailable', 'calculateStock']),
    handleSpan({ row, column, rowIndex, columnIndex }) {
      return iTable.handleSpan(row, columnIndex, this.columns)
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('product/profile/fetch', { ...attributes })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew() {
      this.$router.push({ name: 'product-profile-create' })
    },
    edit(record) {
      this.$store.commit('product/profile/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'product-profile-edit',
        params: { id: record.product_id }
      })
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch('product/profile/destroy', record.product_id)
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    async allowForSale(record) {
      record.loading_sale = true
      let response = await this.saleAvailable(record.product_id)
      record.is_available_for_sale = response.is_available_for_sale
      record.loading_sale = false
    },
    async calculate(record) {
      record.loading_stock = true
      let response = await this.calculateStock(record.product_id)
      record.is_calculate_stock = response.is_calculate_stock
      record.loading_stock = false
    },
    viewSeril(record) {
      this.$router.push({
        name: 'product-profile-serail',
        params: { id: record.product_id }
      })
    },
    allSerial() {
      this.$router.push({ name: 'product-profile-all-serail' })
    },
    async exportPlus() {
      if (this.exportingPlus) return
      this.exportingPlus = true
      try {
        const response = await this.$store.dispatch('product/profile/exportPlus', {
          filter: { search: this.search }
        })
        FileDownload(response.data, 'product-profile-plus.xlsx')
      } catch (error) {
        this.$toasted.error(this.$t('productProfile.exportPlusFailed'))
      } finally {
        this.exportingPlus = false
      }
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('product/profile/RESET_STATE')
    next()
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.fetchData()
    })
  }
}
</script>
<style>
.ivu-table-cell-tree {
  margin-right: 10px;
}
.ivu-table-small i {
  font-size: 14px;
}
.vendor-list {
  display: table;
  border-collapse: collapse;
  border: none;
}
.vendor-list td {
  padding: 2px 4px;
  border-top: none !important;
  font-size: 10px;
}
</style>
